import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// Order List API
export const getOrderList = createAsyncThunk(
    'admin/getOrderList',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('orders/admin/list', payload);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch order list');
        }
    },
);

// Partner Orders API
export const getPartnerOrdersList = createAsyncThunk(
    'admin/getPartnerOrdersList',
    async (payload = {}, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('orders/delivery-partner-orders', {
                params: payload
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch partner orders');
        }
    },
);

export const updateOrderStatus = createAsyncThunk(
    'admin/updateOrderStatus',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/admin/accept', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update order status',
            );
        }
    },
);

export const cancelOrder = createAsyncThunk(
    'admin/cancelOrder',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/admin/cancel', payload);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to cancel order');
        }
    },
);

export const readyPickupOrder = createAsyncThunk(
    'admin/readyPickupOrder',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/admin/ready-pickup', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to mark order ready for pickup',
            );
        }
    },
);

export const deliveredOrder = createAsyncThunk(
    'admin/deliveredOrder',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/admin/delivered', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to mark order as delivered',
            );
        }
    },
);

export const getPartnersList = createAsyncThunk(
    'admin/getPartnersList',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('/delivery-partners/partners');
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch partners list',
            );
        }
    },
);

// Product Summary API
export const getOrderProductSummary = createAsyncThunk(
    'admin/getOrderProductSummary',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('orders/product-summary', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch order product summary',
            );
        }
    },
);

// Driver Assignments API
export const getDriverAssignments = createAsyncThunk(
    'admin/getDriverAssignments',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('/orders/driver-assignments');
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch driver assignments',
            );
        }
    },
);

// Custom Capture Amount API
export const customCaptureAmount = createAsyncThunk(
    'admin/customCaptureAmount',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/custom-capture-amount', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to process custom capture amount',
            );
        }
    },
);

export const getUserList = createAsyncThunk(
    'admin/getUserList',
    async (params = {}, { rejectWithValue }) => {
        try {
            const payload = {
                action: 'user-list',
                draw: 1,
                start: 0,
                ...params,
            };
            const { data } = await axiosInstance.post(
                '/admin/users/admin/users/datatable/user-list',
                payload,
            );
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch user list');
        }
    },
);

export const getUserRoles = createAsyncThunk(
    'admin/getUserRoles',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('/admin/users/admin/users/roles');
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch user roles');
        }
    },
);

export const updateUser = createAsyncThunk(
    'admin/updateUser',
    async (userData, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.put('/admin/users/admin/users/update', userData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update user');
        }
    },
);

export const updateUserStatus = createAsyncThunk(
    'admin/updateUserStatus',
    async (statusData, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.put('/admin/users/admin/users/status', statusData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update user status');
        }
    },
);

export const deleteUserAccount = createAsyncThunk(
    'admin/deleteUserAccount',
    async (deleteData, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.delete('/admin/users/admin/users/delete', {
                data: deleteData,
            });
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to delete user account',
            );
        }
    },
);

// Admin Product Management API
export const fetchAdminProductList = createAsyncThunk(
    'admin/fetchAdminProductList',
    async (params = {}, { rejectWithValue }) => {
        try {
            const payload = {
                action: 'product-list',
                draw: 0,
                start: 0,
                length: 500,
                ...params,
            };
            const { data } = await axiosInstance.post('/products/datatable/product-list', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch admin product list',
            );
        }
    },
);

export const updateProductStatus = createAsyncThunk(
    'admin/updateProductStatus',
    async ({ product_id, status }, { rejectWithValue }) => {
        try {
            const payload = {
                action: 'change-product-status',
                product_id,
                status,
            };
            const { data } = await axiosInstance.post('/products/change-product-status', payload);
            return { data, product_id, status };
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update product status',
            );
        }
    },
);

export const createProduct = createAsyncThunk(
    'admin/createProduct',
    async (productData, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/products/add-product', productData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create product');
        }
    },
);

export const updateProduct = createAsyncThunk(
    'admin/updateProduct',
    async ({ productId, productData }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post(`/products/update-product`, {
                ...productData,
                product_id: productId,
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update product');
        }
    },
);

export const getOrderTypes = createAsyncThunk(
    'admin/getOrderTypes',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('/products/get-order-types');
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch order types');
        }
    },
);

export const addOrderType = createAsyncThunk(
    'admin/addOrderType',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/products/add-order-type', payload);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add order type');
        }
    },
);

export const fetchProductDetails = createAsyncThunk(
    'admin/fetchProductDetails',
    async (productId, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(`/products/product-details/${productId}`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch product details',
            );
        }
    },
);

// Notifications Management API
export const fetchNotificationsList = createAsyncThunk(
    'admin/fetchNotificationsList',
    async (params = {}, { rejectWithValue }) => {
        try {
            const payload = {
                action: 'notifications-list',
                draw: params.draw || 1,
                start: params.start || 0,
                // length: params.length || 10,
                ...params,
            };
            const { data } = await axiosInstance.post('/notifications/list', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch notifications list',
            );
        }
    },
);

export const updateNotificationStatus = createAsyncThunk(
    'admin/updateNotificationStatus',
    async ({ notificationId, status }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post(
                `/notifications/notifications/${notificationId}/status?status_value=${status}`
            );
            return { data, notificationId, status };
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update notification status',
            );
        }
    },
);

export const fetchNotificationDetails = createAsyncThunk(
    'admin/fetchNotificationDetails',
    async (notificationId, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(
                `/notifications/notifications/${notificationId}`,
            );
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch notification details',
            );
        }
    },
);

export const updateNotificationDetails = createAsyncThunk(
    'admin/updateNotificationDetails',
    async (notificationData, { rejectWithValue }) => {
        try {
            const { notification_id, ...updateData } = notificationData;
            const { data } = await axiosInstance.put(
                `notifications/notifications/${notification_id}`,
                updateData,
            );
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update notification details',
            );
        }
    },
);

export const fetchOrderStatuses = createAsyncThunk(
    'admin/fetchOrderStatuses',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(
                '/notifications/notification-types/default/statuses',
            );
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch order statuses',
            );
        }
    },
);

export const fetchUserDataWithId = createAsyncThunk(
    'admin/fetchUserDataWithId',
    async (user_id, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(`/auth/me/${user_id}`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch product details',
            );
        }
    },
);

// Order Tracking List API
export const getOrderTrackingList = createAsyncThunk(
    'admin/getOrderTrackingList',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('orders/order-tracking-list', {
                params: payload,
            });
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch order tracking list',
            );
        }
    },
);

// Get Drivers List API
export const getDriversList = createAsyncThunk(
    'admin/getDriversList',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('orders/drivers');
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch drivers list');
        }
    },
);

// Get Delivery Partners List API
export const getDeliveryPartnersList = createAsyncThunk(
    'admin/getDeliveryPartnersList',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('menus/delivery-partners');
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch delivery partners list',
            );
        }
    },
);

// Cut-off Times Management API
export const getCutOffTimes = createAsyncThunk(
    'admin/getCutOffTimes',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('orders/cut-off-times');
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch cut-off times',
            );
        }
    },
);

export const manageCutOffTime = createAsyncThunk(
    'admin/manageCutOffTime',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('orders/cut-off-times', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to manage cut-off time',
            );
        }
    },
);

export const getUnCapturedOrdersList = createAsyncThunk(
    'admin/getPartnersList',
    async (orderType = 'delivery-partner', { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(`/orders/uncaptured-orders?order_type=${orderType}`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch uncaptured orders list',
            );
        }
    },
);

export const customPrintLabel = createAsyncThunk(
    'admin/customPrintLabel',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/custom-print-label', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to Create Custom Print Label',
            );
        }
    },
);

export const fetchCustomPrintLabel = createAsyncThunk(
    'admin/fetchCustomPrintLabel',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(`/orders/custom-print-label/${payload}`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to Create Custom Print Label',
            );
        }
    },
);

export const updateDriverAssignment = createAsyncThunk(
    'admin/updateDriverAssignment',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/driver-assignments', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update driver assignment',
            );
        }
    },
);

export const deleteDriverAssignment = createAsyncThunk(
    'admin/deleteDriverAssignment',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/driver-assignments', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to delete driver assignment',
            );
        }
    },
);
export const addDriverAssignment = createAsyncThunk(
    'admin/addDriverAssignment',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/driver-assignments', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to add driver assignment',
            );
        }
    },
);

// Preference Info API
export const getPreferenceInfo = createAsyncThunk(
    'admin/getPreferenceInfo',
    async (preference_type, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(`/products/preference-info?preference_type=${preference_type}`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch preference info',
            );
        }
    },
);

// Upload product image
export const uploadProductImage = createAsyncThunk(
    'admin/uploadProductImage',
    async (formData, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/products/product-image-upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to upload product image',
            );
        }
    },
);

// Change Cash Status API
export const changeCashStatus = createAsyncThunk(
    'admin/changeCashStatus',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/change-cash-status', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to change cash status',
            );
        }
    },
);

// Change Delivery Date API
export const changeDeliveryDate = createAsyncThunk(
    'admin/changeDeliveryDate',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/change-delivery-date', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to change delivery date',
            );
        }
    },
);

// Change Delivery Partner API
export const changeDeliveryPartner = createAsyncThunk(
    'admin/changeDeliveryPartner',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/change-delivery-partner', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to change delivery partner',
            );
        }
    },
);

export const fetchPrintOrderInfo = createAsyncThunk(
    'admin/fetchPrintOrderInfo',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/print-multi-order-info', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch print order info',
            );
        }
    },
);

// Update Order Items API
export const updateOrderItems = createAsyncThunk(
    'admin/updateOrderItems',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/admin/update-order-items', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update order items',
            );
        }
    },
);

// Update Order assign drivers API
export const assignDrivers = createAsyncThunk(
    'admin/assignDrivers',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/admin/assign-drivers', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to assign Drivers,',
            );
        }
    },
);

export const fetchOrderTrackingDetails = createAsyncThunk(
    'admin/fetchOrderTrackingDetails',
    async (params, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('/orders/tracking-info',  {
                params: params
            });
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch print order info',
            );
        }
    },
);

export const fetchAdminUserDetails = createAsyncThunk(
    'admin/fetchAdminUserDetails',
    async (user_id, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(`/admin/users/admin/users/${user_id}/details`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch user details',
            );
        }
    },
);

// Get Order Validation List
export const getOrderValidationList = createAsyncThunk(
    'admin/getOrderValidationList',
    async (params = {}, { rejectWithValue }) => {
        try {
            const { page = 1, limit = 10, status = '' } = params;
            const queryParams = new URLSearchParams();
            queryParams.append('page', page);
            queryParams.append('limit', limit);
            if (status !== '') queryParams.append('status', status);

            const { data } = await axiosInstance.get(`/orders/order-validation-list?${queryParams.toString()}`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch order validation list',
            );
        }
    },
);

// Update Order Validation Status
export const updateOrderValidationStatus = createAsyncThunk(
    'admin/updateOrderValidationStatus',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.put('/orders/order-validation-status', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update order validation status',
            );
        }
    },
);

// Get Zelle Payments List
export const getZellePaymentsList = createAsyncThunk(
    'admin/getZellePaymentsList',
    async (params = {}, { rejectWithValue }) => {
        try {
            const { page = 1, limit = 10 } = params;
            const { data } = await axiosInstance.get(`/orders/zelle-payments/list?page=${page}&limit=${limit}`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch zelle payments list',
            );
        }
    },
);

// Map Orders to Zelle Payment
export const mapOrdersToZellePayment = createAsyncThunk(
    'admin/mapOrdersToZellePayment',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post('/orders/zelle-payment/mapping-orders', payload);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || 'Failed to map orders to Zelle payment',
            );
        }
    },
);

export const updateProductAttributes = createAsyncThunk(
  "admin/updateProductAttributes",
  async ({ product_id, out_of_stock }, { rejectWithValue }) => {
    try {
      const payload = {
        product_id,
        out_of_stock,
      };
      const { data } = await axiosInstance.post(
        "/products/update-product-attributes",
        payload
      );
      return { data, product_id, out_of_stock };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update product attributes"
      );
    }
  }
);


export const updateProductQuantity = createAsyncThunk(
  "admin/updateProductQuantity",
  async ({ product_id, quantity }, { rejectWithValue }) => {
    try {
      const payload = {
        product_id,
        quantity,
      };
      const { data } = await axiosInstance.post(
        "/products/update-product-attributes",
        payload
      );
      return { data, product_id, quantity };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update product attributes"
      );
    }
  }
);
